import {
  For,
  Show,
  batch,
  createEffect,
  createSignal,
  onCleanup,
} from "solid-js";
import type { Accessor, JSX } from "solid-js";
import { isElementViewable } from "../utils/viewability";
import { ArrowButton } from "./arrow_button";
import { BannerSlide } from "./banner_slide";
import type { BannerV3 } from "./types";

const DEFAULT_VISIBILITY_THRESHOLD = 0.8;
const DEFAULT_VISIBILITY_DELAY_MS = 300;
const DEFAULT_AUTO_SCROLL_INTERVAL_MS = 3000;
const SWIPE_THRESHOLD = 50;
const DEFAULT_MOBILE_GUTTER_PX = 16;
const DEFAULT_DESKTOP_GUTTER_PX = 16;

export interface BannersProps {
  banners: Accessor<BannerV3[]>;
  isDesktop: Accessor<boolean>;
  enabled?: boolean | Accessor<boolean>;
  onBannerClick: (banner: BannerV3) => void;
  onBannerViewed: (banner: BannerV3) => void;

  visibilityThreshold?: number;
  visibilityDelayMs?: number;
  autoScrollIntervalMs?: number;
  /** Horizontal padding (px) on either side of the banner on mobile. */
  mobileGutterPx?: number;
  /** Horizontal padding (px) on either side of the banner on desktop. */
  desktopGutterPx?: number;
  className?: string;
}

export function Banners(props: BannersProps): JSX.Element {
  const viewedBannerIds = new Set<number>();

  const visibilityThreshold = () =>
    props.visibilityThreshold ?? DEFAULT_VISIBILITY_THRESHOLD;
  const visibilityDelayMs = () =>
    props.visibilityDelayMs ?? DEFAULT_VISIBILITY_DELAY_MS;
  const autoScrollIntervalMs = () =>
    props.autoScrollIntervalMs ?? DEFAULT_AUTO_SCROLL_INTERVAL_MS;
  const mobileGutterPx = () =>
    props.mobileGutterPx ?? DEFAULT_MOBILE_GUTTER_PX;
  const desktopGutterPx = () =>
    props.desktopGutterPx ?? DEFAULT_DESKTOP_GUTTER_PX;

  const isEnabled = () => {
    const value = props.enabled;
    if (value === undefined) return true;
    return typeof value === "function" ? value() : value;
  };

  const v3Banners = () => {
    const allBanners = props.banners();
    return allBanners.filter((banner) => {
      const hasImage = props.isDesktop()
        ? !!banner.desktopImageUrl
        : !!banner.imageUrl;
      if (!hasImage) return false;
      if (banner.type === "BRAND" && banner.brandDiscountPercentage === 0) {
        return false;
      }
      return true;
    });
  };

  const [slideIndex, setSlideIndex] = createSignal(1);
  const [dragOffset, setDragOffset] = createSignal(0);
  const [animate, setAnimate] = createSignal(true);
  const [autoScrollVersion, setAutoScrollVersion] = createSignal(0);
  const [isTouching, setIsTouching] = createSignal(false);
  const [isSlideTransitioning, setIsSlideTransitioning] = createSignal(false);
  let touchStartX = 0;
  let bannerViewportEl: HTMLDivElement | undefined;

  const slides = () => {
    const b = v3Banners();
    if (b.length <= 1) return b;
    return [b[b.length - 1], ...b, b[0]];
  };

  const effectiveSlideIndex = () =>
    v3Banners().length <= 1 ? 0 : slideIndex();

  const activeIndicator = () => {
    const len = v3Banners().length;
    if (len === 0) return 0;
    return (slideIndex() - 1 + len) % len;
  };

  const activeSlideElement = () =>
    bannerViewportEl?.querySelector<HTMLElement>(
      `[data-banner-slide-index="${effectiveSlideIndex()}"]`
    );

  const checkBannerVisibility = () => {
    const slide = activeSlideElement();
    if (!slide) return false;
    return isElementViewable(slide, {
      minimumVisibleRatio: visibilityThreshold(),
    });
  };

  const trackCurrentBannerView = () => {
    const currentBanners = v3Banners();
    if (currentBanners.length === 0) return;

    const currentIndex = activeIndicator();
    const banner = currentBanners[currentIndex];
    if (!banner) return;

    if (viewedBannerIds.has(banner.id)) return;
    viewedBannerIds.add(banner.id);

    props.onBannerViewed(banner);
  };

  const evaluateBannerViewability = () => {
    if (isSlideTransitioning()) return false;
    if (!checkBannerVisibility()) return false;
    trackCurrentBannerView();
    return true;
  };

  function next() {
    if (v3Banners().length <= 1) return;
    const len = v3Banners().length;
    if (slideIndex() >= len + 1) return;
    setAnimate(true);
    setIsSlideTransitioning(true);
    setSlideIndex((i) => i + 1);
  }

  function prev() {
    if (v3Banners().length <= 1) return;
    if (slideIndex() <= 0) return;
    setAnimate(true);
    setIsSlideTransitioning(true);
    setSlideIndex((i) => i - 1);
  }

  function resetAutoScroll() {
    setAutoScrollVersion((v) => v + 1);
  }

  function onTouchStart(e: TouchEvent) {
    setIsTouching(true);
    touchStartX = e.touches[0].clientX;
    setDragOffset(0);
  }

  function onTouchMove(e: TouchEvent) {
    setDragOffset(e.touches[0].clientX - touchStartX);
  }

  function onTouchEnd() {
    setIsTouching(false);
    const delta = dragOffset();
    setDragOffset(0);

    if (delta < -SWIPE_THRESHOLD) {
      next();
    } else if (delta > SWIPE_THRESHOLD) {
      prev();
    }

    resetAutoScroll();
  }

  function handleBoundaryReset() {
    const len = v3Banners().length;
    const idx = slideIndex();
    if (idx === 0) {
      batch(() => {
        setAnimate(false);
        setSlideIndex(len);
      });
    } else if (idx === len + 1) {
      batch(() => {
        setAnimate(false);
        setSlideIndex(1);
      });
    }
  }

  function onTransitionEnd() {
    handleBoundaryReset();
    setIsSlideTransitioning(false);
    evaluateBannerViewability();
  }

  createEffect(() => {
    const idx = slideIndex();
    const len = v3Banners().length;
    if (len <= 1) return;
    if (isSlideTransitioning() || idx === 0 || idx === len + 1) {
      const timeoutId = setTimeout(() => {
        handleBoundaryReset();
        setIsSlideTransitioning(false);
        evaluateBannerViewability();
      }, 350);
      onCleanup(() => clearTimeout(timeoutId));
    }
  });

  function goToIndicator(index: number) {
    if (index === activeIndicator()) return;
    setAnimate(true);
    setIsSlideTransitioning(true);
    setSlideIndex(index + 1);
    resetAutoScroll();
  }

  createEffect(() => {
    autoScrollVersion();
    if (v3Banners().length <= 1) return;
    if (isTouching()) return;
    const intervalId = setInterval(next, autoScrollIntervalMs());
    onCleanup(() => clearInterval(intervalId));
  });

  const attachViewportObserver = (el: HTMLDivElement) => {
    bannerViewportEl = el;
    let visibilityTimer: number | undefined;
    let visibilityFrame: number | undefined;
    let canMeetViewportThreshold = typeof IntersectionObserver === "undefined";

    const clearVisibilityTimer = () => {
      if (visibilityTimer !== undefined) {
        window.clearTimeout(visibilityTimer);
        visibilityTimer = undefined;
      }
    };

    const requestVisibilityCheck = () => {
      if (visibilityFrame !== undefined) return;

      visibilityFrame = window.requestAnimationFrame(() => {
        visibilityFrame = undefined;

        if (!canMeetViewportThreshold) {
          clearVisibilityTimer();
          return;
        }

        if (!checkBannerVisibility()) {
          clearVisibilityTimer();
          return;
        }

        if (visibilityTimer !== undefined) return;
        visibilityTimer = window.setTimeout(() => {
          visibilityTimer = undefined;
          evaluateBannerViewability();
        }, visibilityDelayMs());
      });
    };

    const observer =
      typeof IntersectionObserver === "undefined"
        ? undefined
        : new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              canMeetViewportThreshold =
                entry.isIntersecting &&
                entry.intersectionRatio >= visibilityThreshold();

              if (canMeetViewportThreshold) {
                requestVisibilityCheck();
              } else {
                clearVisibilityTimer();
              }
            },
            { threshold: [visibilityThreshold()] }
          );

    observer?.observe(el);
    window.addEventListener("scroll", requestVisibilityCheck, {
      passive: true,
    });
    window.addEventListener("resize", requestVisibilityCheck);
    requestVisibilityCheck();

    onCleanup(() => {
      observer?.disconnect();
      clearVisibilityTimer();
      if (visibilityFrame !== undefined) {
        window.cancelAnimationFrame(visibilityFrame);
      }
      window.removeEventListener("scroll", requestVisibilityCheck);
      window.removeEventListener("resize", requestVisibilityCheck);
    });
  };

  return (
    <Show when={isEnabled() && v3Banners().length > 0}>
      <div
        data-bring-in-view="banners"
        class={
          props.className ??
          "md:gap flex flex-col items-center gap-3 pb-2"
        }
      >
        <div
          ref={attachViewportObserver}
          // The desktop gutter is a margin, not padding, so the overflow clip
          // lands exactly on the active slide's edges and neighbouring slides
          // never peek into the gutter. `self-stretch` keeps the viewport
          // full-width inside the centred flex column while honouring margins.
          // `touch-pan-y`: browser owns vertical panning (page scroll works
          // when the finger starts on the banner), JS owns horizontal — the
          // gesture arbiter picks a direction on start and doesn't scroll
          // the page during a horizontal swipe.
          class="touch-pan-y self-stretch overflow-hidden"
          style={
            props.isDesktop()
              ? {
                  "margin-left": `${desktopGutterPx()}px`,
                  "margin-right": `${desktopGutterPx()}px`,
                }
              : undefined
          }
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div
            class="flex md:grid md:gap-4 md:pl-0"
            classList={{
              "transition-transform duration-300 ease-in-out":
                animate() && dragOffset() === 0,
            }}
            style={{
              ...(props.isDesktop()
                ? {
                    "grid-template-columns": `repeat(${slides().length}, 100%)`,
                    transform: `translateX(calc(-${effectiveSlideIndex()} * (100% + 16px) + ${dragOffset()}px))`,
                  }
                : {
                    // The gutter is applied as margin on the track so the
                    // track's border box equals its content box. Slide widths
                    // (100% of the track content box) and the translate (100%
                    // of the track border box) then agree, and everything is
                    // relative to this widget's container rather than 100vw.
                    "margin-left": `${mobileGutterPx()}px`,
                    "margin-right": `${mobileGutterPx()}px`,
                    gap: `${mobileGutterPx()}px`,
                    transform: `translateX(calc(-${effectiveSlideIndex()} * (100% + ${mobileGutterPx()}px) + ${dragOffset()}px))`,
                  }),
            }}
            onTransitionEnd={onTransitionEnd}
          >
            <For each={slides()}>
              {(banner, index) => (
                <BannerSlide
                  banner={banner}
                  index={index()}
                  totalBanners={v3Banners().length}
                  isDesktop={props.isDesktop}
                  onBannerClick={props.onBannerClick}
                />
              )}
            </For>
          </div>
        </div>

        <Show when={v3Banners().length > 1}>
          <div class="flex items-center gap-3">
            <ArrowButton
              direction="left"
              onClick={() => {
                prev();
                resetAutoScroll();
              }}
            />

            <div class="flex items-center gap-1">
              <For each={v3Banners()}>
                {(_, index) => (
                  <div
                    class="flex cursor-pointer items-center md:h-5"
                    onClick={() => goToIndicator(index())}
                    role="button"
                    aria-label={`Go to banner ${index() + 1}`}
                  >
                    <div
                      class="h-1 w-3 rounded-full transition-all md:w-4"
                      classList={{
                        "bg-stroke-solid": activeIndicator() === index(),
                        "bg-stroke-2": activeIndicator() !== index(),
                      }}
                    />
                  </div>
                )}
              </For>
            </div>

            <ArrowButton
              direction="right"
              onClick={() => {
                next();
                resetAutoScroll();
              }}
            />
          </div>
        </Show>
      </div>
    </Show>
  );
}
